import {
  Box,
  Button,
  Checkbox,
  Grid,
  Group,
  LoadingOverlay,
  Portal,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useGetDebtor } from 'api/index';
import { PageTitle } from 'components/PageTitle';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import countries from 'i18n-iso-countries';
import { i18nKeys, tKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useLoadCommunicationLocales } from 'modules/Settings/Configuration/api';
import { useMemo } from 'react';
import { useHistory, useParams } from 'react-router';
import { isDefined, isNonNullish } from 'remeda';
import { useConfirmBeforeLeaving } from 'shared/hooks/use-confirm-before-leaving';
import { useCreateClient } from 'shared/hooks/use-create-client';
import { useFetchAddress } from 'shared/hooks/use-fetch-address';
import { useUpdateClientInformation } from 'shared/hooks/use-update-client-information';
import { treatClient } from 'shared/serializer';
import { AVAILABLE_CURRENCIES } from 'types/currency';

import { useClientForm } from '../hooks/useClientForm';

import { ClientMultiPhoneInput } from './ClientMultiPhoneInput';
import { MultiEmailInputDefault } from './MultiEmailInputDefault';
import { MultiIbanBicInput } from './MultiIbanBicInput';

type ClientFormProps = {
  // If this prop is defined, we are in a modal and will use it as the save handler.
  // No better way to type this for now.
  onSuccess?: (data: any) => void;
};

export const ClientForm = ({ onSuccess }: ClientFormProps) => {
  const { communicationLanguages } = useLoadCommunicationLocales();
  const { t, currentLang } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const history = useHistory();
  const areWeInAModal = isDefined(onSuccess);
  const isEditMode = isNonNullish(id);
  const isCreateMode = !isEditMode;

  const { fetchAddress, isFetchAddressLoading } = useFetchAddress();
  const { updateClientInfo, isUpdateClientInfoLoading } = useUpdateClientInformation();
  const { createClient, isCreateClientLoading } = useCreateClient();

  const {
    data: debtorDetailsResponse,
    isLoading,
    isError,
    refetch,
  } = useGetDebtor(id, undefined, { query: { enabled: isEditMode } });

  const debtor = debtorDetailsResponse?.data;
  const client = useMemo(() => (debtor ? treatClient(debtor) : undefined), [debtor]);
  const { form, handleFormErrors } = useClientForm(client);

  useConfirmBeforeLeaving({ hasUnsavedChanges: form.isDirty });

  if (isEditMode && isLoading && !debtorDetailsResponse) {
    return (
      <Box h="100%" pos="relative">
        <LoadingOverlay visible />
      </Box>
    );
  }

  if (isEditMode && (isError || !debtorDetailsResponse)) {
    return (
      <Box>
        <Text>{t(i18nKeys.CLIENT.GENERAL_INFO_TAB.COULD_NOT_FETCH)}</Text>
      </Box>
    );
  }

  const countriesForSelect = Object.entries(countries.getNames(currentLang)).map(
    ([value, description]) => ({ value, description }),
  );

  const handleLoadAddress = (vatNumber: string) => {
    fetchAddress(
      { vat_number: vatNumber },
      {
        onSuccess: data => {
          form.setValues({
            addressAttributes: {
              city: data.city ?? '',
              zipCode: data.zip_code ?? '',
              streetName: data.street_name ?? '',
              streetNumber: data.street_number ?? '',
              streetBox: data.street_box ?? '',
              countryCode: data.country_code ?? '',
              additionalLine1: data.additional_line_1 ?? '',
              additionalLine2: data.additional_line_2 ?? '',
            },
            fullName: data.company_name ?? '',
          });
        },
      },
    );
  };

  const handleSaveClient = () => {
    if (isCreateMode) {
      createClient(
        { data: form.values },
        {
          onSuccess: val => {
            form.resetDirty();
            onSuccess?.(treatClient(val.data));
            if (areWeInAModal) modals.closeAll();
            // @ts-expect-error incorrectly typed by our illustrious predecessors
            else history.push(`/clients/${val.data.data.id}`);
          },
        },
      );
      return;
    }

    updateClientInfo(
      { id, data: form.values },
      {
        onSuccess: async () => {
          form.resetDirty();
          await refetch();
          history.goBack();
        },
      },
    );
  };

  return (
    <>
      {areWeInAModal && (
        <Portal target="#save-client-button-outlet">
          <SaveButton
            disabled={!form.isDirty()}
            loading={isUpdateClientInfoLoading || isCreateClientLoading}
            form="clientForm"
            type="submit"
          />
        </Portal>
      )}
      {!areWeInAModal && (
        <PageTitle>
          <PageTitle.Back />
          {isEditMode ? t(i18nKeys.FORM.CLIENT.EDIT_RECORD) : t(i18nKeys.NEW_CLIENT)}
          <PageTitle.Actions>
            <Button
              disabled={!form.isDirty()}
              loading={isUpdateClientInfoLoading || isCreateClientLoading}
              form="clientForm"
              type="submit"
            >
              {t(i18nKeys.SAVE)}
            </Button>
          </PageTitle.Actions>
        </PageTitle>
      )}
      <form onSubmit={form.onSubmit(handleSaveClient, handleFormErrors)} id="clientForm">
        <Grid>
          <Grid.Col span={6}>
            <Panel title={t(tKeys.CONTACT_PERSON.BASIC_INFORMATION)} h="100%">
              <Stack>
                <TextInput
                  key={form.key('fullName')}
                  label={t(i18nKeys.FORM.CLIENT.FULL_NAME)}
                  placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.FULL_NAME)}
                  {...form.getInputProps('fullName')}
                />
                <Group grow>
                  <TextInput
                    label={t(i18nKeys.VAT_NUMBER)}
                    placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.VAT_NUMBER)}
                    {...form.getInputProps('vatNumber')}
                  />
                  <TextInput
                    label={t(i18nKeys.DEBTOR.REFERENCE)}
                    placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.REFERENCE)}
                    {...form.getInputProps('reference')}
                  />
                </Group>
                <Group grow>
                  <Select
                    label={t(i18nKeys.FORM.CLIENT.CURRENCY)}
                    data={AVAILABLE_CURRENCIES.map(currency => ({
                      value: currency.code,
                      label: currency.description,
                    }))}
                    clearable
                    placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.CURRENCY)}
                    {...form.getInputProps('currency')}
                  />
                  <Select
                    label={t(i18nKeys.FORM.CLIENT.CONTACT_LANG)}
                    data={communicationLanguages.map(lang => ({
                      value: lang.value,
                      label: lang.description,
                    }))}
                    clearable
                    placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.LOCALE)}
                    {...form.getInputProps('locale')}
                  />
                </Group>
                <Stack gap="xl" mt="sm">
                  <Checkbox
                    label={t(i18nKeys.FORM.CLIENT.LEGAL_ENTITY)}
                    {...form.getInputProps('moralEntity', { type: 'checkbox' })}
                  />
                  <Group>
                    <Checkbox
                      {...form.getInputProps('coContractor', { type: 'checkbox' })}
                      onChange={event => {
                        form.setFieldValue('coContractor', event.target.checked);
                        form.setFieldValue('intracom', false);
                      }}
                      label={t(i18nKeys.FORM.CLIENT.COCONTRACTOR_VAT0)}
                    />
                    <Checkbox
                      {...form.getInputProps('intracom', { type: 'checkbox' })}
                      onChange={event => {
                        form.setFieldValue('intracom', event.target.checked);
                        form.setFieldValue('coContractor', false);
                      }}
                      label={t(i18nKeys.FORM.CLIENT.INTRA_COMMUNITY_VAT0)}
                    />
                  </Group>
                </Stack>
              </Stack>
            </Panel>
          </Grid.Col>
          <Grid.Col span={6}>
            <Panel
              title={t(tKeys.ADDRESS)}
              h="100%"
              topRight={
                <Tooltip
                  label={t(i18nKeys.FORM.CLIENT.VAT_TOOLTIP)}
                  disabled={form.values.vatNumber.length > 9}
                >
                  <Button
                    variant="light"
                    disabled={form.values.vatNumber.length < 9}
                    loading={isFetchAddressLoading}
                    size="compact-sm"
                    onClick={() => handleLoadAddress(form.values.vatNumber)}
                  >
                    {t(i18nKeys.FORM.CLIENT.PREFILL_VAT)}
                  </Button>
                </Tooltip>
              }
            >
              <Stack>
                <Group grow align="start">
                  <TextInput
                    label={t(i18nKeys.FORM.ADDRESS.STREET)}
                    placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.STREET)}
                    {...form.getInputProps('addressAttributes.streetName')}
                  />
                  <TextInput
                    label={t(i18nKeys.NUMBER)}
                    placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.STREET_NUMBER)}
                    {...form.getInputProps('addressAttributes.streetNumber')}
                  />
                  <TextInput
                    label={t(i18nKeys.FORM.ADDRESS.BOX)}
                    placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.STREET_BOX)}
                    {...form.getInputProps('addressAttributes.streetBox')}
                  />
                </Group>
                <Group align="start">
                  <TextInput
                    label={t(i18nKeys.FORM.ADDRESS.CITY)}
                    placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.CITY)}
                    {...form.getInputProps('addressAttributes.city')}
                  />
                  <TextInput
                    label={t(i18nKeys.FORM.ADDRESS.POSTAL_CODE)}
                    placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.POSTAL_CODE)}
                    {...form.getInputProps('addressAttributes.zipCode')}
                  />
                  <Select
                    flex={1}
                    label={t(i18nKeys.FORM.ADDRESS.COUNTRY)}
                    searchable
                    clearable
                    placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.COUNTRY)}
                    data={countriesForSelect.map(country => ({
                      value: country.value,
                      label: country.description,
                    }))}
                    {...form.getInputProps('addressAttributes.countryCode')}
                  />
                </Group>
                <TextInput
                  label={t(i18nKeys.ADDITIONAL_LINE_1)}
                  placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.ADDITIONAL_LINE_1)}
                  {...form.getInputProps('addressAttributes.additionalLine1')}
                />
                <TextInput
                  label={t(i18nKeys.ADDITIONAL_LINE_2)}
                  placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.ADDITIONAL_LINE_2)}
                  {...form.getInputProps('addressAttributes.additionalLine2')}
                />
              </Stack>
            </Panel>
          </Grid.Col>

          <Grid.Col span={7}>
            <MultiEmailInputDefault form={form} />
          </Grid.Col>
          <Grid.Col span={5}>
            <ClientMultiPhoneInput form={form} />
          </Grid.Col>
          <Grid.Col span={7}>
            <MultiIbanBicInput form={form} />
          </Grid.Col>
        </Grid>
      </form>
    </>
  );
};
